import React, {useState} from 'react';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import Divider from '@mui/material/Divider';
import PropTypes from 'prop-types';
import TwoToneCheckbox from '../../TwoToneCheckbox.jsx';

/**
 * @param {CheckboxWithOptionsAccountLines.propTypes} props
 * @return {JSX.Element}
 * @constructor
 */
export function CheckboxWithOptionsAccountLines(props) {
    const [anchorEl, setAnchorEl] = useState(null);
    const [currentPageCheckbox, setCurrentPageCheckbox] = useState(false);
    const [allPagesCheckbox, setAllPagesCheckbox] = useState(false);

    const startIndex = props.page * props.rowsPerPage;
    const endIndex = props.rowsPerPage === -1 ? props.accountsUsed.length : Math.min(startIndex + props.rowsPerPage, props.accountsUsed.length);

    const areAllAccountsSelected = props.selectedAccounts.filter((selectedAccount) => selectedAccount.materialId === props.materialId).length === props.accountsUsed.length;

    const isAllAccountsIndeterminate = props.selectedAccounts.some((selectedAccount) => selectedAccount.materialId === props.materialId) &&
        props.selectedAccounts.filter((selectedAccount) => selectedAccount.materialId === props.materialId).length < props.accountsUsed.length;

    const toggleMenu = (event) => {
        if (anchorEl) {
            setAnchorEl(null);
        } else {
            setAnchorEl(event.currentTarget);
        }
    };

    const handleNestedCheckboxChange = (option) => {
        if (option === 'current') {
            if (allPagesCheckbox) {
                setAllPagesCheckbox(false);
            }
            if (!currentPageCheckbox) {
                selectCurrentPageAccounts();
                setCurrentPageCheckbox(true);
            } else {
                deselectCurrentPageAccounts();
                setCurrentPageCheckbox(false);
            }
        } else if (option === 'all') {
            if (currentPageCheckbox) {
                setCurrentPageCheckbox(false);
            }
            if (!areAllAccountsSelected) {
                selectAllAccounts();
                setAllPagesCheckbox(true);
            } else if (areAllAccountsSelected && !allPagesCheckbox) {
                setAllPagesCheckbox(true);
            } else {
                deselectAllAccounts();
                setAllPagesCheckbox(false);
            }
        }
    };
    const selectCurrentPageAccounts = () => {
        const accountsToSelect = Array.from({length: endIndex - startIndex}, (_, idx) => ({
            accountId: startIndex + idx,
            materialId: props.materialId,
        }));

        const updatedSelection = [...accountsToSelect];
        accountsToSelect.forEach((account) => {
            if (!updatedSelection.some((selected) => selected.accountId === account.accountId && selected.materialId === account.materialId)) {
                updatedSelection.push(account);
            }
        });

        props.onSelectAllAccounts(updatedSelection);
    };


    const deselectCurrentPageAccounts = () => {
        const accountsToDeselect = new Set(Array.from({length: endIndex - startIndex}, (_, idx) => startIndex + idx));

        const updatedSelection = props.selectedAccounts.filter((selectedAccount) =>
            !accountsToDeselect.has(selectedAccount.accountId) || selectedAccount.materialId !== props.materialId);

        props.onSelectAllAccounts(updatedSelection);
    };


    const selectAllAccounts = () => {
        const allAccountIdsForMaterial = props.accountsUsed.map((_, idx) => ({accountId: idx, materialId: props.materialId}));

        props.onSelectAllAccounts((prevSelected) => {
            return [...prevSelected.filter((selection) => selection.materialId !== props.materialId), ...allAccountIdsForMaterial];
        });
    };

    const deselectAllAccounts = () => {
        props.onSelectAllAccounts((prevSelected) => prevSelected.filter((selectedAccount) => selectedAccount.materialId !== props.materialId));
    };

    return (
        <div style={{marginLeft: '-0.5rem'}}>
            <IconButton
                onClick={toggleMenu}
                disabled={props.disabled}
                sx={{
                    '&:hover': {
                        backgroundColor: 'transparent',
                    },
                    '& .MuiTouchRipple-root': {
                        display: 'none',
                    },
                }}>
                <TwoToneCheckbox
                    checked={areAllAccountsSelected}
                    indeterminate={isAllAccountsIndeterminate}
                    disabled={props.disabled}
                />
            </IconButton>
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={() => setAnchorEl(null)}
            >
                <MenuItem>
                    <FormGroup>
                        <FormControlLabel
                            control={
                                <TwoToneCheckbox
                                    checked={currentPageCheckbox}
                                    onChange={() => handleNestedCheckboxChange('current')}
                                    disabled={props.disabled}
                                />
                            }
                            label="Select accounts in displayed page"
                        />
                    </FormGroup>
                </MenuItem>
                <Divider />
                <MenuItem>
                    <FormGroup>
                        <FormControlLabel
                            control={
                                <TwoToneCheckbox
                                    checked={allPagesCheckbox}
                                    onChange={() => handleNestedCheckboxChange('all')}
                                    disabled={props.disabled}
                                />
                            }
                            label="Select accounts in all pages"
                        />
                    </FormGroup>
                </MenuItem>
            </Menu>
        </div>
    );
}

CheckboxWithOptionsAccountLines.propTypes = {
    selectedAccounts: PropTypes.array.isRequired,
    materialId: PropTypes.number,
    accountsUsed: PropTypes.array.isRequired,
    onSelectAllAccounts: PropTypes.func.isRequired,
    page: PropTypes.number.isRequired,
    rowsPerPage: PropTypes.number.isRequired,
    disabled: PropTypes.bool,
};
